import {
  Container,
  ContainerGutter,
  Select,
  styled,
} from '@volkswagen-onehub/components-core';
import React, {useEffect, useState, useCallback} from 'react';

const CTAStyled = styled.button`
  color: rgb(255, 255, 255);
  background: rgb(0, 30, 80);
  border: 0;
  padding: 15px;
  min-height: 44px;
  min-width: 44px;
  padding: 0px 32px;
  margin: 0px;
  border-radius: 500px;
  cursor: pointer;

  &:focus {
    outline: none;
    background-color: rgb(0, 119, 204);
    color: rgb(255, 255, 255);
    box-shadow: rgb(0, 30, 80) 0px 0px 0px 1px inset;
  }
`;

type Props = {
  labels: any;
  language: string;
  models: Array<any>;
  distances: Array<number>;
  compareCallback: Function;
  isShowroomPage: boolean;
  preselectedCar: string;
  setSelectedTrimOnLoad: Function;
  tracking: any;
  contentService: any;
};

const CarSelector = (props: Props) => {
  const [selectedCar, setSelectedCar] = useState('0');
  const [selectedTrim, setSelectedTrim] = useState('0');
  const [selectedTrimName, setSelectedTrimName] = useState('');
  const [selectedDist, setSelectedDist] = useState('15000');
  const [trims, setTrims] = useState<Array<any>>([]);
  const [firstLoad, setFirstLoad] = useState(false);
  const [defaultLoaded, setDefaultLoaded] = useState<boolean>(false);

  const {
    labels,
    language,
    models,
    distances,
    compareCallback,
    isShowroomPage,
    preselectedCar,
    setSelectedTrimOnLoad,
    tracking,
    contentService,
  } = props;

  const _renderModels = () => {
    return models.map((model, index) => {
      const key = `${model.name}-${index}`;
      return (
        <option className="options" key={key} value={model.value}>
          {language === 'en'
            ? `${model.year} ${model.name}`
            : `${model.name} ${model.year}`}
        </option>
      );
    });
  };

  const _renderTrims = () => {
    return trims.map((trim, index) => {
      const key = `${trim.id}-${index}`;
      return (
        <option className="options" key={key} value={trim.id}>
          {' '}
          {trim.name}{' '}
        </option>
      );
    });
  };

  const _renderDistances = () => {
    return distances.map((distance, index) => {
      const key = `${distance}-${index}`;
      const localeValue = new Intl.NumberFormat(`${language}-CA`).format(
        distance,
      );
      const display = labels.distance.replace('{distance}', localeValue);
      return (
        <option className="options" key={key} value={distance}>
          {display}
        </option>
      );
    });
  };

  const _changeCarSelected = (
    event: React.SyntheticEvent<HTMLSelectElement, Event>,
  ) => {
    const value: string = event.currentTarget.value;
    setSelectedCar(value);
    let selectedTrims: Array<any> = [];
    models.forEach((car) => {
      if (car.value === value) {
        selectedTrims = car.trims;
      }
    });

    const defaultTrim = selectedTrims.filter(x => x.default === true)[0].id || selectedTrims[0].id;
    setTrims(selectedTrims);
    setSelectedTrim(defaultTrim);
  };

  if (isShowroomPage) {
    if (selectedCar !== preselectedCar) {
      setSelectedCar(preselectedCar);
      setFirstLoad(true);
    }
  }

  useEffect(() => {
    let selectedTrims: Array<any> = [];
    models.forEach((car) => {
      if (car.value === selectedCar) {
        selectedTrims = car.trims;
      }
    });
    setTrims(selectedTrims);
  }, [selectedCar, models]);

  useEffect(() => {
    if (trims.length > 0 && preselectedCar === selectedCar && firstLoad) {
      const defaultTrim = trims.find((trim: any) => trim.default === true);
      setSelectedTrim(defaultTrim.id);
      setSelectedTrimOnLoad(defaultTrim.name);
    }
  }, [trims, selectedCar, setSelectedTrimOnLoad, preselectedCar]);

  const _changeSelector = (
    event: React.SyntheticEvent<HTMLSelectElement, Event>,
    setterFunc: Function,
  ) => {
    const value: string = event.currentTarget.value;
    const trim: string = event.currentTarget.selectedOptions[0].innerText;
    setSelectedTrimName(trim);
    setterFunc(value);
  };

  const _prepopulateTable = useCallback(
    async (isDefault?: boolean) => {
      let selectedTrimData = selectedTrim;
      let selectedCarData = selectedCar;

      if (isDefault && selectedTrim == '0' && selectedCar == '0') {
        selectedCarData = 'tiguan 2025';
        selectedTrimData = '142587';
      }

      let selectedCarObject = {};

      models.forEach((car) => {
        if (car.value === selectedCarData) {
          selectedCarObject = car;
        }
      });

      if (selectedTrimData !== '0') {
        await compareCallback(
          selectedDist,
          selectedTrimData,
          selectedCarObject,
        );
      } else if (selectedCarData === '0') {
        document.getElementById('model-select')?.focus();
      } else if (selectedTrimData === '0') {
        document.getElementById('trims-select')?.focus();
      }
    },
    [models, selectedCar, selectedDist, selectedTrim],
  );

  const _continueHandler = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    _prepopulateTable();
    const selectedModel: any = models.filter(
      (model) => model.value === selectedCar,
    );
    let filterlist;
    if (isShowroomPage) {
      filterlist = [
        {
          type: 'single_value',
          name: 'TR',
          values: [selectedTrimName],
        },
        {
          type: 'single_value',
          name: 'DI',
          values: [selectedDist],
        },
      ];
    } else {
      filterlist = [
        {
          type: 'single_value',
          name: 'CG',
          values: [`${selectedModel[0].year} ${selectedModel[0].name}`],
        },
        {
          type: 'single_value',
          name: 'TR',
          values: [selectedTrimName],
        },
        {
          type: 'single_value',
          name: 'DI',
          values: [selectedDist],
        },
      ];
    }

    const data = {
      environment: {
        featureAppId: 'CostOfOwnership-D6-CA',
      },
      link: {
        name: labels.form_continue,
        url: window.location.href,
      },
      filter: {
        numberOfResults: undefined,
        filterlist,
        sortOrder: [
          {
            sortby: 'Total maintenance cost',
            order: 'asc',
          },
        ],
      },
    };

    tracking.track('VWBasic_FilterSearchButton_Click', data);
  };

  useEffect(() => {
    if (firstLoad) {
      _prepopulateTable();
      setTimeout(function () {
        setFirstLoad(false);
      }, 1000);
    }
  }, [selectedTrim, _prepopulateTable, firstLoad]);

  useEffect(() => {
    if (isShowroomPage) {
      setSelectedDist('25000');
    }
  }, [isShowroomPage]);

  useEffect(() => {
    if (!defaultLoaded && contentService?.content) {
      if (!contentService.content.year || !contentService.content.modelCode) {
        setSelectedCar('tiguan 2025');
        setSelectedTrim('142587');
        _prepopulateTable(!defaultLoaded);
      }

      setDefaultLoaded(true);
    }
  }, []);

  return (
    <Container gutter={ContainerGutter.dynamic0040} stretchContent wrap="auto">
      {!isShowroomPage ? (
        // <div className="models">
        // 		<label htmlFor="model-select">{ labels.form_model }</label>
        // 		<select id="model-select" name="model" value={selectedCar} onChange={ e => _changeCarSelected(e) }>
        // 		<option className="options" value="0"> { labels.model_default }</option>
        // 		{ _renderModels() }
        // 	</select>
        // </div>

        <Select
          name="model"
          value={selectedCar}
          onChange={(e) => _changeCarSelected(e)}
          label={labels.form_model}
          aria-label={labels.form_model}
        >
          <option className="options" value="0">
            {' '}
            {labels.model_default}
          </option>
          {_renderModels()}
        </Select>
      ) : null}

      <Select
        label={labels.form_trim}
        aria-label={labels.form_trim}
        name="trim"
        value={selectedTrim}
        onChange={(e) => _changeSelector(e, setSelectedTrim)}
      >
        {_renderTrims()}
      </Select>

      <Select
        label={labels.form_distance}
        aria-label={labels.form_distance}
        name="distance"
        value={selectedDist}
        onChange={(e) => _changeSelector(e, setSelectedDist)}
      >
        {_renderDistances()}
      </Select>

      <CTAStyled
        disabled={selectedCar == '0' || selectedTrim == '0'}
        data-click-gtm="true"
        data-gtm-value={labels.tracking}
        onClick={_continueHandler}
        type="submit"
      >
        {labels.form_continue}
      </CTAStyled>
    </Container>
  );
};

export default CarSelector;
